import { useState } from 'react';
import { createPortal } from 'react-dom';
import type { Funcionario } from '@/context/FuncionariosContext';
import { useModalAnimation } from '@/hooks/useModalAnimation';
import { useModalOverflow } from '@/hooks/useModalOverflow';
import { calcularProgressao } from '@/utils/progressaoUtils';

interface EmailAlertModalProps {
  elegiveis: Funcionario[];
  pontosPorFuncionario: Record<string, number>;
  onClose: () => void;
}

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;

export default function EmailAlertModal({ elegiveis, pontosPorFuncionario, onClose }: EmailAlertModalProps) {
  const { animating, handleClose } = useModalAnimation(onClose);
  useModalOverflow(true);
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  async function handleEnviar() {
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Introduza um endereço de email válido.');
      return;
    }
    setSending(true);
    setError('');
    try {
      const payload = {
        destinatario: email,
        ciclo: '2023–2024',
        elegiveis: elegiveis.map((f) => {
          const pontos = pontosPorFuncionario[f.id] ?? 0;
          const prog = calcularProgressao(pontos, f.nivelAtual);
          return {
            nome: f.nome,
            carreira: f.carreira,
            nivelAtual: f.nivelAtual,
            proximoNivel: prog.proximoNivel,
            pontosAtuais: pontos,
          };
        }),
      };

      const res = await fetch(`${SUPABASE_URL}/functions/v1/send-progressao-alert`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? 'Erro ao enviar email.');
      setSent(true);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Erro ao enviar email.');
    } finally {
      setSending(false);
    }
  }

  const modal = (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" onClick={handleClose}>
      <div className={`absolute inset-0 bg-black/50 backdrop-blur-sm ${animating ? 'modal-backdrop-active' : 'modal-backdrop-enter'}`} />
      <div
        className={`relative bg-white rounded-2xl w-full max-w-5xl max-h-[96vh] overflow-hidden ${animating ? 'modal-panel-active' : 'modal-panel-enter'}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center bg-slate-100 rounded-xl text-slate-600 flex-shrink-0">
              <i className="ri-mail-send-line text-xl" />
            </div>
            <div>
              <h2 className="font-bold text-slate-800 text-base">Enviar Alerta por Email</h2>
              <p className="text-xs text-slate-400 mt-0.5">{elegiveis.length} funcionário(s) elegível(is)</p>
            </div>
          </div>
          <button onClick={handleClose} className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors cursor-pointer">
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-4">
          {sent ? (
            <div className="flex flex-col items-center gap-3 py-6 text-center">
              <div className="w-14 h-14 flex items-center justify-center bg-emerald-100 rounded-full text-emerald-600">
                <i className="ri-checkbox-circle-line text-3xl" />
              </div>
              <p className="font-semibold text-slate-800">Email enviado com sucesso!</p>
              <p className="text-sm text-slate-500">O alerta foi enviado para <strong>{email}</strong> com a lista de {elegiveis.length} funcionário(s) elegível(is).</p>
              <button onClick={handleClose} className="whitespace-nowrap mt-2 px-5 py-2 text-sm bg-slate-800 text-white rounded-lg hover:bg-slate-700 transition-colors cursor-pointer">
                Fechar
              </button>
            </div>
          ) : (
            <>
              {/* Lista resumida */}
              <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4">
                <p className="text-xs font-semibold text-emerald-700 mb-2">Funcionários incluídos no alerta</p>
                <div className="space-y-1.5 max-h-36 overflow-y-auto">
                  {elegiveis.map((f) => {
                    const pontos = pontosPorFuncionario[f.id] ?? 0;
                    return (
                      <div key={f.id} className="flex items-center justify-between text-xs">
                        <span className="font-medium text-slate-700">{f.nome}</span>
                        <span className="text-emerald-700 font-semibold">{pontos} pts</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Campo email */}
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">Endereço de Email *</label>
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setError(''); }}
                  placeholder="ex: rh@camara.pt"
                  className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg bg-white text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-300"
                />
              </div>

              <div className="bg-slate-50 rounded-lg px-3 py-2.5 flex items-start gap-2">
                <div className="w-4 h-4 flex items-center justify-center text-slate-400 mt-0.5 flex-shrink-0">
                  <i className="ri-information-line text-sm" />
                </div>
                <p className="text-xs text-slate-500 leading-relaxed">
                  O email inclui a lista completa de elegíveis com nome, carreira, nível atual → novo nível e pontos acumulados. Requer a chave <strong>RESEND_API_KEY</strong> configurada nas variáveis de ambiente do Supabase.
                </p>
              </div>

              {error && (
                <div className="flex items-center gap-2 text-red-600 text-xs bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                  <i className="ri-error-warning-line text-sm" />
                  {error}
                </div>
              )}
            </>
          )}
        </div>

        {!sent && (
          <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-end gap-3">
            <button onClick={handleClose} className="whitespace-nowrap px-4 py-2 text-sm text-slate-600 hover:text-slate-800 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer">
              Cancelar
            </button>
            <button
              onClick={handleEnviar}
              disabled={sending}
              className="whitespace-nowrap px-5 py-2 text-sm bg-slate-800 text-white rounded-lg hover:bg-slate-700 transition-colors cursor-pointer flex items-center gap-2 disabled:opacity-60"
            >
              {sending ? (
                <><div className="w-4 h-4 flex items-center justify-center"><i className="ri-loader-4-line animate-spin text-sm" /></div>A enviar...</>
              ) : (
                <><div className="w-4 h-4 flex items-center justify-center"><i className="ri-send-plane-line text-sm" /></div>Enviar Alerta</>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}